import { type ReactNode } from "react";
import { cn } from "@/lib/utils";
import type { Finding } from "@/lib/qa";
import { FindingList } from "@/components/qa/FindingList";

/**
 * A labelled form control with its QA findings underneath.
 *
 * Y-05: the label is always associated with the control, and any finding is
 * wired to it through aria-describedby so a screen reader announces it.
 */
export function Field({
  id,
  label,
  hint,
  required,
  findings = [],
  children,
  className,
}: {
  id: string;
  label: string;
  hint?: string;
  required?: boolean;
  findings?: Finding[];
  children: ReactNode;
  className?: string;
}) {
  const hintId = hint ? `${id}-hint` : undefined;
  const findingId = findings.length ? `${id}-findings` : undefined;

  return (
    <div className={cn("space-y-1", className)}>
      <label htmlFor={id} className="block text-sm font-medium text-slate-700">
        {label}
        {required && (
          <span className="ml-1 text-blocking" aria-hidden="true">
            *
          </span>
        )}
        {required && <span className="sr-only"> (required)</span>}
      </label>

      {hint && (
        <p id={hintId} className="text-xs text-slate-500">
          {hint}
        </p>
      )}

      <div aria-describedby={cn(hintId, findingId) || undefined}>{children}</div>

      {findings.length > 0 && <FindingList id={findingId} findings={findings} />}
    </div>
  );
}
